import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import type { VaultDocumentCategory } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { Upload, FileText, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { VAULT_CATEGORY_LABELS } from '@/lib/vault-document.constants'
import { formatFileSize } from '@/utils/format-file-size'

// ── Upload Document Dialog ──

export const UPLOAD_ACCEPT =
  '.pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.mdx,.json,.png,.jpg,.jpeg,.webp,.gif'
export const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
  })
}

export function UploadDocumentDialog({
  open,
  onOpenChange,
  onUploaded,
  keyDocumentKey,
}: {
  keyDocumentKey?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploaded: (documentIds: string[]) => void | Promise<void>
}) {
  const { workspaceId } = useParams({ strict: false })
  const [files, setFiles] = useState<File[]>([])
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState<VaultDocumentCategory>('REFERENCE')
  const [description, setDescription] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadedIds, setUploadedIds] = useState<string[]>([])

  const uploadMutation = useMutation(
    trpc.vaultDocument.uploadFile.mutationOptions()
  )

  const handleFiles = (nextFiles: File[]) => {
    const accepted = nextFiles.filter((nextFile) => {
      if (nextFile.size <= MAX_FILE_SIZE) return true
      toast.error(`${nextFile.name} is too large. Maximum size is 10 MB.`)
      return false
    })
    if (accepted.length === 0) return

    setFiles(accepted)
    setUploadedIds([])
    if (accepted.length === 1 && !title.trim()) {
      const ext = accepted[0].name.lastIndexOf('.')
      setTitle(ext > 0 ? accepted[0].name.slice(0, ext) : accepted[0].name)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleFiles(Array.from(e.dataTransfer.files))
  }

  const handleSubmit = async () => {
    if (
      isSubmitting ||
      files.length === 0 ||
      (files.length === 1 && !title.trim())
    )
      return
    setIsSubmitting(true)
    try {
      const batchId = files.length > 1 ? crypto.randomUUID() : null
      const documentIds = [...uploadedIds]
      for (const uploadFile of files.slice(documentIds.length)) {
        const ext = uploadFile.name.lastIndexOf('.')
        const fallbackTitle =
          ext > 0 ? uploadFile.name.slice(0, ext) : uploadFile.name
        const base64 = await fileToBase64(uploadFile)
        const uploaded = await uploadMutation.mutateAsync({
          filename: uploadFile.name,
          contentType: uploadFile.type || 'application/octet-stream',
          base64Data: base64,
          size: uploadFile.size,
          category,
          title: files.length === 1 ? title.trim() : fallbackTitle,
          description: description.trim() || null,
          batchId,
          ...(keyDocumentKey ? { keyDocumentKey } : {}),
        })
        documentIds.push(uploaded.id)
        setUploadedIds([...documentIds])
      }
      await onUploaded(documentIds)
      toast.success(
        files.length === 1
          ? 'Document uploaded'
          : `${files.length} documents uploaded`
      )
      setFiles([])
      setTitle('')
      setDescription('')
      setUploadedIds([])
      onOpenChange(false)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not finish uploading the documents.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const reset = () => {
    setFiles([])
    setUploadedIds([])
    setTitle('')
    setDescription('')
    setCategory('REFERENCE')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (isSubmitting) return
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      <DialogContent className='!max-w-[600px] overflow-hidden'>
        <DialogHeader>
          <DialogTitle className='text-[16px] font-semibold'>
            Upload Document
          </DialogTitle>
          <DialogDescription className='text-text-subtle text-[13px]'>
            Upload files to your document vault for your compliance program.
          </DialogDescription>
        </DialogHeader>
        <div className='mt-2 min-w-0 space-y-4 px-6'>
          {/* Drop zone */}
          {files.length === 0 ? (
            <label
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-sm border-2 border-dashed px-4 py-8 transition-colors ${
                dragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-border-divider bg-surface-subtle hover:border-primary hover:bg-primary/5'
              }`}
            >
              <Upload className='text-text-placeholder mb-2 h-6 w-6' />
              <span className='text-text-body text-[13px] font-medium'>
                Drag & drop files or click to select
              </span>
              <span className='text-text-placeholder mt-0.5 text-[11px]'>
                PDF, Word, Excel, CSV, Text, Images up to 10 MB each
              </span>
              <input
                type='file'
                multiple
                accept={UPLOAD_ACCEPT}
                className='hidden'
                onChange={(e) => {
                  handleFiles(Array.from(e.target.files ?? []))
                }}
              />
            </label>
          ) : (
            <div className='border-border bg-surface-subtle flex w-full min-w-0 items-center gap-3 rounded-sm border px-3 py-2.5'>
              <FileText className='text-primary h-5 w-5 shrink-0' />
              <div className='min-w-0 flex-1'>
                <div className='text-text-secondary truncate text-[13px] font-medium'>
                  {files.length === 1
                    ? files[0].name
                    : `${files.length} files selected`}
                </div>
                <div className='text-text-placeholder text-[11px]'>
                  {files.length === 1
                    ? formatFileSize(files[0].size)
                    : `${formatFileSize(
                        files.reduce((sum, nextFile) => sum + nextFile.size, 0),
                        ''
                      )} total`}
                </div>
              </div>
              <button
                disabled={isSubmitting}
                onClick={() => {
                  setFiles([])
                  setUploadedIds([])
                }}
                className='text-text-placeholder hover:text-text-body shrink-0 text-[11px]'
              >
                Remove
              </button>
            </div>
          )}

          {/* Title */}
          {files.length <= 1 ? (
            <div className='space-y-1.5'>
              <Label>Title</Label>
              <Input
                disabled={isSubmitting || uploadedIds.length > 0}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='Document title'
                className='border-border-divider h-9 rounded-md text-[12px]'
              />
            </div>
          ) : null}

          {/* Category */}
          <div className='space-y-1.5'>
            <Label>Category</Label>
            <Select
              disabled={isSubmitting || uploadedIds.length > 0}
              value={category}
              onValueChange={(v) => setCategory(v as VaultDocumentCategory)}
            >
              <SelectTrigger
                fullWidth
                className='border-border-divider h-9 text-[12px]'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(VAULT_CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k} className='text-[12px]'>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {category === 'POLICY' && (
            <div className='border-border bg-selected rounded-md border px-4 py-3'>
              <p className='font-body text-text-secondary text-[12px] font-medium'>
                For compliance policies, use the dedicated import flow
              </p>
              <p className='font-body text-text-body mt-0.5 text-[11px]'>
                Policy import classifies your document against the policy
                catalog, extracts testable claims, and links to compliance
                controls automatically.
              </p>
              <Link
                to='/workspaces/$workspaceId/policies'
                params={{ workspaceId: workspaceId ?? '' }}
                className='font-body text-primary mt-2 inline-flex items-center gap-1 text-[12px] font-medium hover:underline'
                onClick={() => onOpenChange(false)}
              >
                Go to Policies → Import
                <ArrowRight className='h-3 w-3' />
              </Link>
            </div>
          )}

          {/* Description */}
          <div className='space-y-1.5'>
            <Label>
              Description{' '}
              <span className='text-text-placeholder font-normal'>
                (optional)
              </span>
            </Label>
            <Textarea
              disabled={isSubmitting || uploadedIds.length > 0}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Brief description of this document'
              className='border-border-divider min-h-[60px] rounded-md text-[12px]'
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant='outline'
            disabled={isSubmitting}
            className='h-9 rounded-sm text-[13px]'
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button
            className='bg-primary text-primary-foreground hover:bg-primary/90 h-9 rounded-sm text-[13px]'
            onClick={handleSubmit}
            disabled={
              files.length === 0 ||
              (files.length === 1 && !title.trim()) ||
              isSubmitting
            }
          >
            {isSubmitting
              ? 'Saving...'
              : files.length > 1
                ? 'Upload batch'
                : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
